import '.d{display:flex;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.l3{padding:0.5rem ;}.ssr-plugin'
import '.cq{border-radius:0.75rem;}.ssr-plugin'
import '.cr{font-size:1.125rem;}.ssr-plugin'
import '.cs{height:1.75rem;}.ssr-plugin'
import '.ac{flex-grow:1;}.ssr-plugin'
import '.l{padding:0px;}.ssr-plugin'
import '.aa{min-width:0px;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.by{border:none;}.ssr-plugin'
import '.ct{outline:none;}.ssr-plugin'
import '.cu{background:transparent;}.ssr-plugin'
import '.b6{text-align:center;}.ssr-plugin'
import { Style, styled } from 'inlines'
import {
  useCallback,
  KeyboardEvent,
  forwardRef,
  useState,
  useRef,
  KeyboardEventHandler,
  ClipboardEventHandler,
  useEffect,
} from 'react'
import useIsomorphicLayoutEffect from '@app/util/useIsomorphicLayoutEffect'
import { InputField } from '../InputField'
import Row from '@app/components/layout/Row'
import useMediaQuery from '@app/util/useMediaQuery'

const textToCode = (text: string = null, codeLength: number): string[] => {
  return (text ?? '')
    .slice(0, codeLength)
    .padEnd(codeLength, '\0')
    .split('')
    .map((char) => (char === '\0' ? '' : char))
}

export type VerificationCodeInputProps = {
  text?: string
  placeholder?: 'numbers'
  codeLength?: number
  label?: string
  disabled?: boolean
  errorMessage?: string
  style?: Style
  setText: (text: string) => void
}

export const VerificationCodeInput = ({
  text,
  placeholder: _placeholder,
  codeLength = 6,
  label,
  disabled,
  errorMessage,
  style,
  setText,
}: VerificationCodeInputProps) => {
  const errorStateOn = !disabled && !!errorMessage

  const isMobile = useMediaQuery()
  const [code, setCode] = useState<string[]>(textToCode(text, codeLength))
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (text === '') {
      setCode(textToCode(null, codeLength))
    }
  }, [text, codeLength])

  const setCodeText = (text: string, index: number) => {
    const filtered = text.replace(/\D/g, '').slice(0, 1)
    setCode((prev) => {
      prev[index] = filtered
      return [...prev]
    })
    if (filtered.length === 1 && index + 1 < codeLength) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const onCodeInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.metaKey || e.ctrlKey) {
      return
    } else if (e.key === 'Backspace') {
      if (code[index].length === 0 && index > 0) {
        setCodeText('', index - 1)
        inputRefs.current[index - 1]?.focus()
      }
      return
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
      moveCursorToTheEnd(inputRefs.current[index - 1])
    } else if (e.key === 'ArrowRight' && index < codeLength - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
      moveCursorToTheEnd(inputRefs.current[index + 1])
    } else if (e.key.length === 1 && !/\d/.test(e.key)) {
      e.preventDefault()
    } else if (
      /\d/.test(e.key) &&
      code[index].length === 1 &&
      index < codeLength - 1
    ) {
      setCodeText(e.key, index + 1)
      e.preventDefault()
    }
  }

  const moveCursorToTheEnd = (ref: HTMLInputElement | null) => {
    ref.selectionStart = 1
    ref.selectionEnd = 1
  }

  useIsomorphicLayoutEffect(() => {
    if (code.every((c) => c === '')) {
      inputRefs.current[0]?.focus()
    }
  }, [code])

  useEffect(() => {
    setText(code.join(''))
  }, [code, setText])

  return (
    <InputField
      label={label}
      errorMessage={errorStateOn ? errorMessage : undefined}
      style={{
        ...style,
      }}
    >
      <Row gap={isMobile ? '0.5rem' : '1rem'} style={{}}>
        {Array.from({ length: codeLength }, (v, k) => k + 1).map((i) => {
          const index = i - 1
          return (
            <SymbolInput
              key={index}
              text={code[index]}
              placeholder={`${i}`}
              errorStateOn={false}
              disabled={disabled}
              setText={(text) => {
                setCodeText(text, index)
              }}
              onKeyDown={(e) => {
                onCodeInputKeyDown(e, index)
              }}
              onPaste={(e) => {
                e.preventDefault()
                const pastedText = e.clipboardData
                  .getData('text/plain')
                  .replace(/\D/g, '')
                  .slice(0, codeLength)
                setCode(textToCode(pastedText, codeLength))
                setText(pastedText)
              }}
              ref={(el) => {
                inputRefs.current[index] = el
              }}
            />
          )
        })}
      </Row>
    </InputField>
  )
}

type SymbolInputProps = {
  text: string
  placeholder: string
  errorStateOn: boolean
  disabled: boolean
  setText: (text: string) => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  onPaste?: ClipboardEventHandler<HTMLInputElement>
}

const SymbolInput = forwardRef<HTMLInputElement, SymbolInputProps>(
  (
    {
      text,
      placeholder,
      errorStateOn,
      disabled,
      setText,
      onKeyDown,
      onPaste,
    }: SymbolInputProps,
    ref,
  ) => {
    const border = useCallback(
      (color: string) =>
        `1px solid var(${errorStateOn ? '--brand-primary' : color})`,
      [errorStateOn],
    )

    return (
      <styled.div className="d t l3 cq"
        style={{
          border: border('--palette-blue-300'),
          '&:hover': {
            border: border('--palette-blue-500'),
          },
          '&:focus-within': {
            border: border('--brand-secondary'),
          },
          '&:has(:disabled)': {
            border: 'var(--border-default)',
            background: 'var(--gray-100)',
          },
          background: errorStateOn
            ? 'var(--palette-red-100)'
            : 'var(--palette-white)',
        }}
      >
        <styled.input className="cr cs ac l aa n by ct cu b6"
          ref={ref}
          placeholder={placeholder}
          value={text}
          inputMode="numeric"
          pattern="[0-9]*"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          maxLength={1}
          onPaste={onPaste}
          style={{
            '&::placeholder': {
              color: errorStateOn ? 'var(--status-error)' : 'var(--gray-300)',
              userSelect: 'none',
            },
            '&:disabled': {
              cursor: 'not-allowed',
            },
          }}
        ></styled.input>
      </styled.div>
    )
  },
)

SymbolInput.displayName = 'SymbolInput'
